/**
 * Membership Service
 *
 * Handles user-to-team membership operations via the Membership_Service Lambda.
 * Includes the multi-team manager-assignment flow (Requirement 6).
 */

import apiClient from './api';

const unwrap = (response) => response.data?.data ?? response.data;

/**
 * Assign a single user to multiple active teams in one transaction.
 *
 * Submits 2-20 distinct `team_id` values to
 * `POST /api/v1/users/{user_id}/team-assignment` (R6.1).
 *
 * The backend rejects out-of-range lists with HTTP 400 (R6.6) and rolls back
 * the whole transaction if any individual `team_id` fails validation,
 * returning an error that identifies the failing `team_id` (R6.2).
 *
 * @param {string} userId - Cognito sub / user id of the manager.
 * @param {string[]} teamIds - Distinct active team ids (2-20 entries).
 */
export const assignManagerTeams = async (userId, teamIds) => {
  try {
    const response = await apiClient.post(
      `/users/${userId}/team-assignment`,
      { team_ids: teamIds },
      { skipActiveTeamHeader: true }
    );
    return unwrap(response);
  } catch (error) {
    console.error('Failed to assign manager teams:', error);
    throw error;
  }
};

/**
 * List the teams a user belongs to.
 */
export const getUserTeams = async (userId) => {
  try {
    const response = await apiClient.get(`/users/${userId}/teams`, {
      skipActiveTeamHeader: true
    });
    const payload = unwrap(response);
    return payload?.teams || payload || [];
  } catch (error) {
    console.error('Failed to fetch user teams:', error);
    throw error;
  }
};
